import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Clock, X } from "lucide-react";
import { SectionHead } from "./Reveal";

const POSTS = [
    {
        id: "two-am-customer",
        category: "Growth",
        readTime: "4 min read",
        title: "The 2 AM Customer: Why Your Business Never Really Closes",
        excerpt:
            "Your next customer is scrolling at midnight, comparing you with three competitors. The question is — what do they find when the shutters are down?",
        body: [
            "Here's a thought that should keep every business owner curious: most buying decisions don't happen during business hours. They happen at 11 PM on a sofa, at 6 AM over chai, in the back of a cab. Your customer is shortlisting you — or skipping you — while your lights are off.",
            "A photograph can only say so much in that moment. It shows one corner, one angle, one carefully chosen second. But a 360° virtual tour hands the keys to the customer. They wander your lobby, check the seating by the window, peek into the private cabin — all at 2 AM, all without asking anyone.",
            "Businesses with virtual tours consistently see longer website visits and stronger enquiry quality, because the people who call have already been there. They're not asking 'what's it like?' anymore. They're asking 'when can I come?'",
            "Your physical space closes every night. Your digital one doesn't have to.",
        ],
    },
    {
        id: "experiences-remembered",
        category: "Psychology",
        readTime: "5 min read",
        title: "Photos Get Skipped. Experiences Get Remembered.",
        excerpt:
            "The brain forgets what it scrolls past, but remembers what it explores. Here's why interactive spaces win trust faster than any photoshoot.",
        body: [
            "Think about the last hundred photos you scrolled past today. How many do you actually remember? Now think about the last place you walked through and looked around freely. You remember the light, the layout, the feeling. That's not an accident — it's how memory works.",
            "Passive looking fades fast. Active exploration sticks. When a customer drags through your space in 360°, choosing where to look and what to zoom into, their brain treats it like a visit, not an advertisement. Attention goes up. Doubt goes down.",
            "Trust follows the same rule. A polished photo can hide a hundred flaws, and customers know it. An open, explorable space says something far more powerful: we have nothing to hide. That single signal does more for credibility than any 'best quality' tagline ever will.",
            "This is why we say: don't just show your space. Let them experience it. The businesses that hand over control are the ones customers feel they already know.",
        ],
    },
    {
        id: "google-maps-trick",
        category: "Local SEO",
        readTime: "4 min read",
        title: "The Google Maps Trick Most Pune Businesses Haven't Tried",
        excerpt:
            "When someone searches 'near me', Google decides who gets seen. A 360° tour on your Business Profile quietly tilts that decision in your favour.",
        body: [
            "Open Google Maps and search for hotels, cafés or gyms around you. Most listings look identical — a name, a rating, a few photos. Now imagine one of them lets you step inside and look around. Which one are you tapping?",
            "Google's own research has long shown that listings with rich visual content — especially virtual tours — earn more interest, more direction requests and more website clicks than those without. For local searches, that engagement is gold: Google notices when people linger on your listing, and rewards it with visibility.",
            "A 360° tour on your Google Business Profile works silently, 24/7. No ad budget, no boosting, no daily posting. Just your real space, doing the convincing while your competitors upload another blurry photo.",
            "The best part? Most local businesses still haven't done it. In almost every category in Pune, the first movers own the advantage. The only question is whether that's you or the shop across the street.",
        ],
    },
    {
        id: "first-ten-seconds",
        category: "Conversion",
        readTime: "4 min read",
        title: "From Click to Customer: What Happens in the First 10 Seconds",
        excerpt:
            "A visitor lands on your page and gives you roughly ten seconds. Here's how those seconds decide whether they stay, explore — or vanish forever.",
        body: [
            "Ten seconds. That's the unspoken deal every visitor offers your website. In that window they decide one thing: is this place worth my time? A wall of text says 'work'. A single photo says 'maybe'. An explorable space says 'come in'.",
            "The magic of a 360° tour in those first seconds is that it demands nothing. No reading, no scrolling, no hunting for information. The visitor simply drags, looks around, and — without realising it — starts imagining themselves inside your space. That imagining is the first step of every purchase.",
            "There's a second, quieter win: time. Every extra moment someone spends exploring your tour is a moment they're not spending on your competitor's tab. Attention is the currency of the internet, and interactive spaces are remarkably good at earning it.",
            "So audit your own first ten seconds. Open your website like a stranger would. If the best thing on the screen is a photo, you already know what to upgrade.",
        ],
    },
    {
        id: "hotel-sells-while-sleeping",
        category: "Hospitality",
        readTime: "5 min read",
        title: "The Hotel That Sells Rooms While It Sleeps",
        excerpt:
            "A family in Delhi is planning a weekend in Pune at 11 PM. Your front desk is closed. Your tour isn't. How immersive previews quietly fill rooms.",
        body: [
            "Picture a family in Delhi, planning a Pune getaway after dinner. They open three hotel tabs. The first shows glossy photos — a bed, a window, a pool at a suspiciously perfect angle. The second, same story. The third lets them stand in the lobby, walk into the room, check the view from the balcony and peek at the breakfast spread.",
            "Which hotel gets the booking? Almost always, the one that removed the doubt. Guests don't fear paying for a room — they fear paying for a surprise. Every unexplained corner of a property is a small gamble in the guest's mind, and gambles delay bookings.",
            "A 360° walkthrough flips that psychology. The room they explored online is the room they expect — and expectation matching reality is the foundation of five-star reviews. Fewer 'not like the photos' complaints, more 'exactly as shown' praise.",
            "The best part is the timing. That family booked at 11:40 PM. No staff involved, no call, no commission to an aggregator chat. Your space did the selling. Your team just handed over the keys.",
        ],
    },
    {
        id: "near-me-value",
        category: "Local SEO",
        readTime: "4 min read",
        title: "Why 'Near Me' Is the Most Valuable Phrase in Your Business",
        excerpt:
            "'Café near me', 'gym near me', 'showroom near me' — these two little words carry buyers with wallets open. Here's how to catch them.",
        body: [
            "Some searchers are browsing. Others are buying. The difference is usually two words: 'near me'. Someone typing 'best cafés in India' is daydreaming. Someone typing 'café near me' has a wallet, a craving, and about fifteen minutes of patience.",
            "Google treats these searches like emergencies of intent — it shows a map, three listings, and little else. If you're in that top three, you win footfall. If you're not, you're invisible at the exact moment money is being spent.",
            "What nudges you into that map pack? Complete profiles, fresh photos, reviews — and engagement. When searchers tap your listing, explore your 360° tour and hit 'directions', Google reads it as 'this place satisfies searchers' and shows you to more of them.",
            "You can't buy your way into lasting local trust, but you can earn it with a listing people love to open. Start with the experience: let them step inside before they step out.",
        ],
    },
];

export default function Blog() {
    const [openPost, setOpenPost] = useState(null);

    return (
        <section id="blog" className="relative py-20 lg:py-32" data-testid="blog-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHead
                    chapter="12"
                    eyebrow="Blog"
                    title="STORIES WORTH EXPLORING"
                    sub="Short, sharp reads on how immersive spaces win customers — brewed fresh from the field."
                />
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {POSTS.map((p, i) => (
                        <motion.article
                            key={p.id}
                            data-testid={`blog-card-${p.id}`}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                            className="glass-card group flex cursor-pointer flex-col rounded-2xl p-7"
                            onClick={() => setOpenPost(p)}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#00C6FF]">
                                    {p.category}
                                </span>
                                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                    <Clock size={11} /> {p.readTime}
                                </span>
                            </div>
                            <h3 className="mt-5 text-lg font-bold leading-snug text-white transition-colors group-hover:text-[#00C6FF]">
                                {p.title}
                            </h3>
                            <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">
                                {p.excerpt}
                            </p>
                            <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#00C6FF]">
                                Read Story
                                <ArrowUpRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </span>
                        </motion.article>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {openPost && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-[#050B14]/90 p-4 backdrop-blur-md"
                        onClick={() => setOpenPost(null)}
                        data-testid="blog-modal"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 40, scale: 0.97 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-card max-h-[82vh] w-full max-w-2xl overflow-y-auto rounded-2xl p-8 sm:p-10"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#00C6FF]">
                                        {openPost.category} · {openPost.readTime}
                                    </span>
                                    <h3 className="mt-3 text-2xl font-bold leading-snug text-white">
                                        {openPost.title}
                                    </h3>
                                </div>
                                <button
                                    data-testid="blog-modal-close"
                                    onClick={() => setOpenPost(null)}
                                    className="rounded-full border border-[#00C6FF]/30 p-2 text-slate-300 hover:text-[#00C6FF]"
                                    aria-label="Close article"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="mt-6 space-y-5">
                                {openPost.body.map((para, i) => (
                                    <p key={i} className="text-[15px] leading-relaxed text-slate-300">
                                        {para}
                                    </p>
                                ))}
                            </div>
                            <p className="mt-8 border-t border-[#00C6FF]/15 pt-5 text-xs text-slate-500">
                                Prime 360 View · Pune — Don't just show your space. Let them
                                experience it.
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
